// Copyright (c) 2026. LodgIQ Inc, All rights reserved
//
// What-if rate simulator widget for Ask LodgIQ. Opened by the agent's
// show_rate_simulator tool, prefilled with the live BAR / recommendation /
// forecast occupancy for one stay date. All slider math runs CLIENT-SIDE
// (mirroring chat/calculators.py rate_change_impact) — dragging costs nothing.
//
// AL-5: in-card date switcher (token-free re-seed via /rate-sim/prefill/),
// Rec + comp-set median markers on the slider track, and a verdict grounded
// against the model's forecast / OTB / unconstrained demand — every number is
// labelled COMPUTED (slider math) or PREDICTED (model), never blended.

import React, { useMemo, useState } from "react";
import { runRateSimPrefill } from "../../services/wizard.service";

const ELASTICITIES = [
  { value: -0.5, label: "Resilient", hint: "demand barely reacts" },
  { value: -1.0, label: "Typical", hint: "proportional reaction" },
  { value: -1.5, label: "Sensitive", hint: "demand reacts strongly" },
];

function moneyFmt(currency) {
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: currency || "USD", maximumFractionDigits: 0 });
  } catch (_) {
    return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 });
  }
}

const niceDate = (iso) => {
  const d = new Date(`${iso}T12:00:00`);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" });
};

export default function RateSimulatorCard({ widget, propertyId, onInterpret }) {
  const [pf, setPf] = useState((widget && widget.prefill) || {});
  const [pfBusy, setPfBusy] = useState(false);
  const [pfErr, setPfErr] = useState("");

  const curRate = Number(pf.current_rate);
  const curOcc = Number(pf.current_occ_pct);
  const capacity = Number(pf.capacity) || null;
  const recommended = pf.recommended_rate != null ? Number(pf.recommended_rate) : null;
  const otbOcc = pf.otb_occ_pct != null ? Number(pf.otb_occ_pct) : null;
  const compMedian = pf.comp_median != null ? Number(pf.comp_median) : null;
  const unconsOcc = pf.uncons_occ_pct != null ? Number(pf.uncons_occ_pct) : null;
  const occSource = pf.occ_source || "forecast"; // pre-AL-5 payloads were forecast-first
  const money = moneyFmt(widget && widget.currency);

  const daysOut = useMemo(() => {
    if (pf.days_out != null) return Number(pf.days_out);
    const d = new Date(`${pf.date}T12:00:00`);
    return Number.isNaN(d.getTime()) ? null : Math.round((d - new Date()) / 86400000);
  }, [pf.date, pf.days_out]);

  const lo = Math.max(1, Math.round(curRate * 0.7));
  const hi = Math.round(curRate * 1.3);
  const [rate, setRate] = useState(Math.round(curRate));
  const [elasticity, setElasticity] = useState(-1.0);

  const track = (event, props) => {
    try { if (window.analyze && window.analyze.track) window.analyze.track(event, props || {}); } catch (_) {}
  };

  const switchDate = async (iso) => {
    if (!propertyId || !iso || iso === pf.date || pfBusy) return;
    setPfBusy(true);
    setPfErr("");
    try {
      const res = await runRateSimPrefill(propertyId, iso);
      const next = res.prefill || {};
      setPf(next);
      if (next.current_rate != null) setRate(Math.round(Number(next.current_rate)));
      track("askq_ratesim_date_switch", { date: iso });
    } catch (err) {
      setPfErr(`No live data for ${niceDate(iso)} — kept ${niceDate(pf.date)}.`);
    } finally {
      setPfBusy(false);
    }
  };

  // ---- scenario engine -----------------------------------------------------
  // The house splits in two, and only one half is for sale:
  //   LOCKED  — rooms already on the books. A BAR change cannot cancel them and
  //             cannot reprice them: they keep OTB ADR (often far below BAR when
  //             group/contract mix is heavy).
  //   IN PLAY — the pickup still expected between OTB and the baseline forecast.
  //             THIS is the only demand a rate move can win or lose, so the
  //             constant-elasticity response (x (P1/P0)^e) applies here alone.
  // The old model applied elasticity to TOTAL occupancy and priced the whole
  // house at the slider rate — it happily projected occupancy BELOW what was
  // already sold (a $71 raise "cancelling" 145 existing bookings) and showed
  // RevPAR ~75% above what the sold rooms' real ADR could ever deliver.
  // Falls back to whole-house math only when the OTB numbers are missing.
  //
  // The split applies whenever OTB is KNOWN. An earlier guard also required
  // otbOcc <= curOcc, on the reasoning that you cannot have booked more than you
  // forecast to sell. But a forecast BELOW on-the-books happens routinely — the
  // constrained forecast lags actual bookings close to arrival, and near a
  // sell-out it simply hasn't caught up (seen live: OTB 99.9%, forecast 99.6%).
  // In that case the old guard fell through to whole-house math and applied
  // elasticity to all 99.6 pts, projecting 73.9% occupancy on a night with ONE
  // room left — i.e. it claimed a rate rise would cancel 268 booked rooms. The
  // sold-out night is precisely where the fallback is most dangerous, so it must
  // never be the branch that handles it.
  //
  // A forecast under OTB is a STALE forecast, not a prediction of cancellations.
  // The baseline is therefore floored at what is already sold.
  const otbAdr = pf.otb_adr != null ? Number(pf.otb_adr) : null;
  const fcstAdr = pf.forecast_adr != null ? Number(pf.forecast_adr) : null;
  const split = otbOcc != null && Number.isFinite(otbOcc);
  const lockedOcc = split ? Math.min(otbOcc, 100) : 0;
  const lockedAdr = split ? (otbAdr != null ? otbAdr : curRate) : 0;
  const baseOcc = split ? Math.max(curOcc, lockedOcc) : curOcc;
  const basePickup = split ? Math.max(0, baseOcc - lockedOcc) : curOcc;
  // Baseline had to be lifted to OTB — worth saying out loud rather than quietly
  // showing a different number from the one on the Forecast tile.
  const baselineFloored = split && lockedOcc > curOcc + 0.001;
  // Nothing left for a rate move to win or lose: every slider position is the same
  // answer, and the card has to say so instead of implying the move did something.
  const noPickup = split && basePickup <= 0.05;

  // What does the remaining pickup ACTUALLY book at? BAR is a reference price
  // point; achieved ADR is revenue / rooms sold across the whole mix (channels,
  // fences, negotiated rates, room-type premiums) — correlated with BAR, never
  // equal to it. The hotel's own forecast implies the answer:
  //   fcst_occ x fcst_adr = locked_occ x otb_adr + pickup_pts x pickup_adr
  // Solving for pickup_adr grounds the revenue math in the achieved blend. When
  // the slider moves BAR by x%, pickup ADR moves proportionally (pass-through
  // via the mix) rather than snapping to the new BAR. Guarded: an implausible
  // implied value (bad feed, forecast < OTB revenue) falls back to BAR.
  const pickupAdr = (() => {
    if (!split || basePickup <= 0) return curRate;
    if (fcstAdr == null || otbAdr == null) return curRate;
    const implied = (baseOcc * fcstAdr - lockedOcc * otbAdr) / basePickup;
    return implied > 0 && implied < curRate * 3 ? implied : curRate;
  })();
  const pickupAdrDerived = pickupAdr !== curRate;

  const scenario = (r, e) => {
    // What the elasticity assumption ASKS for, before the house is consulted.
    const wanted = basePickup * Math.pow(r / curRate, e);
    const pickup = Math.min(100 - lockedOcc, wanted);
    const occ = Math.max(lockedOcc, Math.min(100, lockedOcc + pickup));
    const revpar = (lockedOcc / 100) * lockedAdr
      + (pickup / 100) * pickupAdr * (r / curRate);
    // `clipped` = the modelled demand had nowhere to go. It must be surfaced: a cut
    // whose extra demand cannot be housed does not earn back the discount, and the
    // -1.0 revenue-neutrality the card relies on elsewhere stops holding.
    return { occ, pickup, wanted, clipped: wanted > pickup + 1e-9, revpar };
  };

  const sim = useMemo(() => {
    const chgPct = ((rate - curRate) / curRate) * 100;
    const base = scenario(curRate, elasticity);        // baseline: today's rate
    const now = scenario(rate, elasticity);
    // Break-even: the pickup points the in-play rooms must still deliver at the
    // new rate for RevPAR to hold. (With whole-house fallback this reduces to
    // the old curOcc*curRate/rate.)
    const bePickup = (basePickup * curRate) / rate;
    // beOccRaw can exceed 100 — meaning the move CANNOT break even at any
    // achievable occupancy. Capping to 100 for display made that read as "you need
    // a full house", which on a night already projecting 100% looks satisfied when
    // it is arithmetically impossible. Keep both and let the copy tell the truth.
    const beOccRaw = lockedOcc + bePickup;
    const beOcc = Math.min(100, beOccRaw);
    return {
      chgPct,
      curRevpar: base.revpar,
      beOcc,
      beOccRaw,
      beUnreachable: beOccRaw > 100 + 1e-6,
      bePickup,
      occNew: now.occ,
      occDelta: now.occ - base.occ,
      pickupNew: now.pickup,
      pickupWanted: now.wanted,
      clipped: now.clipped,
      revparNew: now.revpar,
      revparDelta: now.revpar - base.revpar,
      revDeltaDay: capacity ? (now.revpar - base.revpar) * capacity : null,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rate, elasticity, curRate, baseOcc, capacity, lockedOcc, lockedAdr, basePickup]);

  if (!Number.isFinite(curRate) || curRate <= 0 || !Number.isFinite(curOcc)) {
    return <div className="wiz-calc wiz-calc--fallback">The rate simulator couldn't load its inputs. Ask again with a date.</div>;
  }
  if (widget && widget.schema_version && widget.schema_version !== 1) {
    return <div className="wiz-calc wiz-calc--fallback">This simulator needs a newer app version. Please refresh.</div>;
  }

  const up = sim.revparDelta >= 0;
  const flat = Math.abs(sim.revparDelta) < 0.01; // e = -1 is revenue-neutral by construction
  const pct = (v, dp = 1) => `${v >= 0 ? "+" : ""}${v.toFixed(dp)}`;
  const markerPos = (v) => Math.max(0, Math.min(100, ((v - lo) / (hi - lo)) * 100));
  const baselineLabel = occSource === "otb" ? "OTB" : occSource === "override" ? "as given" : "forecast";

  // AL-5 fix 3: the card must never contradict LodgIQ's own recommendation in
  // silence. When the slider sits on (or within $1 of) the recommended rate, say
  // what the MODEL believes, alongside the user's elasticity assumption.
  const atRec = recommended != null && Math.abs(rate - recommended) <= 1;
  const recIsRaise = recommended != null && recommended > curRate;
  const resilientDay = capacity
    ? Math.round((scenario(rate, -0.5).revpar - sim.curRevpar) * capacity)
    : null;

  // grounded read on the break-even line: computed threshold vs the model's PREDICTED baseline
  const cushion = baseOcc - sim.beOcc; // pts the predicted baseline sits above break-even
  const roomsLeft = capacity != null && otbOcc != null
    ? Math.max(0, Math.round((capacity * (100 - otbOcc)) / 100))
    : null;
  // rooms the move actually touches — expected pickup at the baseline, not the house
  const roomsInPlay = capacity != null && split
    ? Math.max(0, Math.round((capacity * basePickup) / 100))
    : null;

  const groundParts = [];
  if (noPickup) {
    // Say it once, plainly. Every slider position returns the same numbers here,
    // and a break-even line has no meaning when there is nothing left to sell.
    groundParts.push(
      `This night is effectively sold out: ${lockedOcc.toFixed(1)}% is already on the books` +
      (roomsLeft != null ? ` (${roomsLeft} room${roomsLeft === 1 ? "" : "s"} left)` : "") +
      `, and the ${baselineLabel} expects no further pickup. A rate change cannot ` +
      `reprice or cancel booked rooms, so it has no modelled effect on this date — ` +
      `the numbers above are the same at every rate.`
    );
  } else if (occSource !== "override" && !sim.beUnreachable) {
    // Suppressed when break-even is unreachable: "N pts below the line" is a weaker,
    // more hopeful statement than "the line cannot be reached", and printing both
    // invites the reader to take the softer one.
    groundParts.push(
      cushion >= 0
        ? `The model's ${baselineLabel} baseline (${baseOcc.toFixed(1)}%) sits ${cushion.toFixed(1)} pts above that line.`
        : `The model's ${baselineLabel} baseline (${baseOcc.toFixed(1)}%) is ${(-cushion).toFixed(1)} pts BELOW that line — the prediction alone doesn't cover this move.`
    );
  }
  if (sim.clipped) {
    const avail = 100 - lockedOcc;
    groundParts.push(
      `Note: at this sensitivity the model wants ${sim.pickupWanted.toFixed(1)} pts of pickup but only ` +
      `${avail.toFixed(1)} pts${roomsLeft != null ? ` (${roomsLeft} room${roomsLeft === 1 ? "" : "s"})` : ""} remain, ` +
      `so the projection is capped by the house — the extra demand this rate would ` +
      `create has nowhere to go.`
    );
  }
  if (baselineFloored && !noPickup) {
    groundParts.push(
      `The ${baselineLabel} (${curOcc.toFixed(1)}%) sits below what is already booked ` +
      `(${lockedOcc.toFixed(1)}%), so the baseline is held at on-the-books — a forecast ` +
      `under OTB is stale, not a prediction of cancellations.`
    );
  }
  if (unconsOcc != null && unconsOcc >= 100 && rate > curRate) {
    groundParts.push(`Predicted demand is ${unconsOcc.toFixed(0)}% of capacity — demand exceeds the house, which favors holding rate.`);
  }

  const interpret = () => {
    if (!onInterpret) return;
    onInterpret(
      `Interpret this rate scenario for ${pf.date}: current rate ${money.format(curRate)} at ${baseOcc.toFixed(1)}% baseline occupancy (${baselineLabel}` +
      (baselineFloored ? `, held at on-the-books because the forecast ${curOcc.toFixed(1)}% is below it` : "") + `); ` +
      `proposed rate ${money.format(rate)} (${pct(sim.chgPct)}%), assumed elasticity ${elasticity}. ` +
      `Projected: occupancy ${sim.occNew.toFixed(1)}% (${pct(sim.occDelta)} pts), RevPAR ${sim.revparNew.toFixed(2)} (${pct(sim.revparDelta, 2)})` +
      (sim.revDeltaDay != null ? `, revenue impact ${money.format(Math.round(sim.revDeltaDay))}/day` : "") +
      (sim.beUnreachable
        ? `. Break-even occupancy would be ${sim.beOccRaw.toFixed(1)}% — ABOVE 100%, so this move cannot hold RevPAR at any achievable occupancy. Lead with that.`
        : `. Break-even occupancy ${sim.beOcc.toFixed(1)}% (constant-elasticity model: at -1.0 the move is revenue-neutral by construction).`) +
      (sim.clipped
        ? ` The modelled demand response is CAPPED by the house: it wants ${sim.pickupWanted.toFixed(1)} pts of pickup but only ${(100 - lockedOcc).toFixed(1)} pts remain, so the extra demand has nowhere to go.`
        : "") +
      (noPickup
        ? ` NOTE: this night is effectively sold out — ${lockedOcc.toFixed(1)}% is on the books and no further pickup is expected, so NOTHING is in play and the rate move has no modelled effect. Say that plainly; do not present the projections as an outcome of the move.`
        : "") +
      (split && !noPickup
        ? ` Model: ${lockedOcc.toFixed(1)}% already booked is LOCKED at OTB ADR${otbAdr != null ? ` ${money.format(otbAdr)}` : ""} — the rate move only affects the ${basePickup.toFixed(1)} pts of remaining pickup${roomsInPlay != null ? ` (~${roomsInPlay} rooms)` : ""}; RevPAR is blended.` +
          (pickupAdrDerived
            ? ` Pickup prices at the ACHIEVED ADR ${money.format(pickupAdr)} implied by the forecast (BAR is a reference price, not what the mix pays), scaling proportionally with the rate move.`
            : "")
        : "") +
      (otbOcc != null ? ` OTB today ${otbOcc}%${roomsLeft != null ? ` (${roomsLeft} rooms left)` : ""}.` : "") +
      (daysOut != null && daysOut >= 0 ? ` ${daysOut === 0 ? "Arrival is today" : `${daysOut} days to arrival`}.` : "") +
      (recommended != null ? ` System recommendation ${money.format(recommended)}.` : "") +
      (compMedian != null ? ` Comp-set median ${money.format(compMedian)}${pf.comp_position ? `, our position ${pf.comp_position}` : ""}.` : "") +
      (unconsOcc != null ? ` Model's unconstrained demand ${unconsOcc}% of capacity.` : "") +
      ` Should I make this move?`
    );
  };

  return (
    <div className="wiz-calc wiz-sim">
      <div className="wiz-calc__head">
        <span className="wiz-calc__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 17l5-6 4 3 7-9" />
            <path d="M14 5h6v6" />
          </svg>
        </span>
        <span className="wiz-calc__title">Rate simulator</span>
        {propertyId ? (
          <span className={`wiz-sim__datepick ${pfBusy ? "is-busy" : ""}`} title="Switch the stay date — live numbers reload, no tokens used">
            <input
              type="date"
              value={pf.date || ""}
              onChange={(e) => switchDate(e.target.value)}
              disabled={pfBusy}
              aria-label="Stay date"
            />
            <span className="wiz-sim__datelabel">{pfBusy ? "loading…" : niceDate(pf.date)}</span>
          </span>
        ) : (
          <span className="wiz-sim__date">{niceDate(pf.date)}</span>
        )}
      </div>

      {pfErr && <div className="wiz-sim__pferr">{pfErr}</div>}

      <div className="wiz-sim__slider-row">
        <span className="wiz-sim__bound">{money.format(lo)}</span>
        <span className="wiz-sim__track">
          <input
            type="range"
            min={lo}
            max={hi}
            step={1}
            value={rate}
            onChange={(e) => setRate(Number(e.target.value))}
            aria-label="Proposed rate"
          />
          {recommended != null && recommended >= lo && recommended <= hi && (
            <span
              className="wiz-sim__marker wiz-sim__marker--rec"
              style={{ left: `${markerPos(recommended)}%` }}
              title={`System recommendation ${money.format(recommended)} — the model's prediction`}
            >
              <i /><b>Rec</b>
            </span>
          )}
          {compMedian != null && compMedian >= lo && compMedian <= hi && (
            <span
              className="wiz-sim__marker wiz-sim__marker--mkt"
              style={{ left: `${markerPos(compMedian)}%` }}
              title={`Comp-set median ${money.format(compMedian)}${pf.comp_as_of ? ` (shopped ${pf.comp_as_of})` : ""}`}
            >
              <i /><b>Mkt</b>
            </span>
          )}
        </span>
        <span className="wiz-sim__bound">{money.format(hi)}</span>
      </div>

      <div className="wiz-sim__readout">
        <span className="wiz-sim__rate">{money.format(rate)}</span>
        <span className={`wiz-sim__chg ${sim.chgPct >= 0 ? "is-up" : "is-down"}`}>
          {pct(sim.chgPct)}% vs current {money.format(curRate)}
        </span>
        <span className="wiz-sim__quick">
          <button onClick={() => setRate(Math.round(curRate))}>Reset</button>
          {recommended != null && (
            <button onClick={() => setRate(Math.round(recommended))} title="Snap to the system recommendation">
              Rec {money.format(recommended)}
            </button>
          )}
          {compMedian != null && (
            <button onClick={() => setRate(Math.round(compMedian))} title="Snap to the comp-set median">
              Mkt {money.format(compMedian)}
            </button>
          )}
        </span>
      </div>

      <div className="wiz-sim__elasticity" role="radiogroup" aria-label="Demand elasticity">
        <span className="wiz-calc__flabel">Demand reaction</span>
        <div className="wiz-sim__seg">
          {ELASTICITIES.map((e) => (
            <button
              key={e.value}
              className={elasticity === e.value ? "is-on" : ""}
              onClick={() => setElasticity(e.value)}
              title={`${e.hint} (elasticity ${e.value})`}
            >
              {e.label}
            </button>
          ))}
        </div>
      </div>

      <div className="wiz-calc__metrics">
        <div className="wiz-calc__metric">
          <span className="wiz-calc__metric-label">Projected occupancy</span>
          <span className="wiz-calc__metric-val">{sim.occNew.toFixed(1)}%</span>
          <span className={`wiz-sim__delta ${sim.occDelta >= 0 ? "is-up" : "is-down"}`}>{pct(sim.occDelta)} pts</span>
        </div>
        <div className="wiz-calc__metric">
          <span className="wiz-calc__metric-label">Projected RevPAR</span>
          <span className="wiz-calc__metric-val">{money.format(Math.round(sim.revparNew))}</span>
          <span className={`wiz-sim__delta ${up ? "is-up" : "is-down"}`}>{pct(sim.revparDelta, 2)}</span>
        </div>
        {sim.revDeltaDay != null && (
          <div className="wiz-calc__metric">
            <span className="wiz-calc__metric-label">Revenue impact / day</span>
            <span className="wiz-calc__metric-val">{money.format(Math.round(sim.revDeltaDay))}</span>
            <span className={`wiz-sim__delta ${up ? "is-up" : "is-down"}`}>
              {roomsInPlay != null ? `on ~${roomsInPlay} rooms in play` : `${capacity} rooms`}
            </span>
          </div>
        )}
      </div>

      <div className={`wiz-calc__verdict wiz-calc__verdict--${flat ? "neutral" : up ? "good" : "bad"}`}>
        {sim.beUnreachable
          ? // Break-even above 100% — a full house still would not pay for this move.
            // Say it outright: capping the number to 100.0% on a night already
            // projecting 100.0% read as "target met" when it cannot be met at all.
            `This move cannot pay for itself: holding RevPAR would need ${sim.beOccRaw.toFixed(1)}% occupancy, and the house only goes to 100%. Even selling every remaining room at this rate leaves RevPAR short.`
          : flat
            ? `Revenue-neutral under this assumption — you'd need occupancy at ${sim.beOcc.toFixed(1)}% to hold RevPAR, which is exactly what a proportional (Typical) demand reaction delivers.`
            : up
              ? `RevPAR improves under this assumption — revenue-neutral as long as occupancy lands at ${sim.beOcc.toFixed(1)}% or better.`
              : `RevPAR declines under this assumption — occupancy must reach ${sim.beOcc.toFixed(1)}% to break even.`}
        {split && !flat && (
          <span className="wiz-sim__ground">
            {` Your ${lockedOcc.toFixed(1)}% on the books is safe either way — the move only bets the ${basePickup.toFixed(1)} pts of pickup still to come.`}
          </span>
        )}
        {groundParts.length > 0 && <span className="wiz-sim__ground"> {groundParts.join(" ")}</span>}
      </div>

      {atRec && (
        <div className="wiz-sim__recnote">
          <span className="wiz-sim__cp-badge">LodgIQ recommends this rate</span>
          <span>
            {`The demand model is pricing this night at ${money.format(recommended)}`}
            {recIsRaise ? ` (${pct(((recommended - curRate) / curRate) * 100)}% vs today's ${money.format(curRate)})` : ""}
            {`. Above, "${ELASTICITIES.find((e) => e.value === elasticity).label}" is YOUR assumption about how demand reacts — `}
            {flat
              ? "at that setting the move is exactly revenue-neutral, so the recommendation pays off only if demand holds up better than proportionally"
              : up
                ? "at that setting the move is accretive"
                : "at that setting the move looks dilutive, which contradicts the model"}
            {resilientDay != null
              ? `. If demand is Resilient, the same move is ${resilientDay >= 0 ? "+" : "−"}${money.format(Math.abs(resilientDay))}/day.`
              : "."}
          </span>
        </div>
      )}

      <div className="wiz-sim__refs">
        {otbOcc != null && <span>OTB today: {otbOcc}%{roomsLeft != null ? ` (${roomsLeft} rooms left)` : ""}</span>}
        {split && otbAdr != null && (
          <span>Booked rooms keep their {money.format(otbAdr)} ADR</span>
        )}
        {split && pickupAdrDerived && (
          <span title="Implied by your forecast: sell rate is a reference price; achieved ADR is the blend of channels, fences and room types that actually books. It moves with your rate proportionally.">
            New pickup prices at ≈{money.format(pickupAdr)} achieved (from your forecast), moving with your rate
          </span>
        )}
        {daysOut != null && daysOut >= 0 && <span>{daysOut === 0 ? "Arrival: today" : `${daysOut} days to arrival`}</span>}
        <span>
          Baseline: {baselineLabel} {curOcc}% at {money.format(curRate)}
          {/* the projections use baseOcc; showing only the stale forecast here
              would leave the footer contradicting the headline number */}
          {baselineFloored && ` — held at OTB ${lockedOcc.toFixed(1)}%`}
        </span>
        {compMedian != null && (
          <span>Market: median {money.format(compMedian)}{pf.comp_position ? ` · you are ${pf.comp_position}` : ""}</span>
        )}
        {unconsOcc != null && <span>Model demand: {unconsOcc}% of capacity</span>}
        <span>Elasticity is an assumption, not a forecast</span>
      </div>

      {onInterpret && (
        <button className="wiz-calc__interpret" onClick={interpret}>Ask LodgIQ to interpret ↗</button>
      )}

      <div className="wiz-calc__foot">© {new Date().getFullYear()} LodgIQ</div>
    </div>
  );
}
